import { FC, useCallback, useState } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { fileBucketUrl } from '@shared/utils/fileUtils';

export interface CarouselImage {
  url: string;
  name: string;
  unitId: string | null;
  brandName: string | null;
}

interface ProductImageCarouselProps {
  images: CarouselImage[];
  carouselIndex: number;
  onIndexChange: (index: number) => void;
  displayBrandName: string | null;
}

const MIN_SWIPE_DISTANCE = 50;

const ProductImageCarousel: FC<ProductImageCarouselProps> = ({
  images,
  carouselIndex,
  onIndexChange,
  displayBrandName,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? null);
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > MIN_SWIPE_DISTANCE) {
      onIndexChange(Math.min(carouselIndex + 1, images.length - 1));
    } else if (distance < -MIN_SWIPE_DISTANCE) {
      onIndexChange(Math.max(carouselIndex - 1, 0));
    }
  }, [touchStart, touchEnd, carouselIndex, images.length, onIndexChange]);

  return (
    <div className="relative">
      <div
        className="aspect-square w-full overflow-hidden bg-stone-200"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
        >
          {images.map((ci, idx) => (
            <div key={idx} className="relative h-full w-full flex-shrink-0">
              {ci.url ? (
                <img
                  src={fileBucketUrl(ci.url, 'w400')}
                  alt={ci.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-stone-400">
                  No Image
                </div>
              )}
              {ci.unitId && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-4 pb-4 pt-8">
                  {ci.brandName && (
                    <span className="mb-1 inline-block rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm">
                      {ci.brandName}
                    </span>
                  )}
                  <p className="text-base font-bold text-white">{ci.name}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 감사 문구 오버레이 */}
      <div className="pointer-events-none absolute left-0 right-0 top-0 bg-gradient-to-b from-black/50 to-transparent px-4 pb-8 pt-3 text-center">
        <p className="text-sm font-bold text-white/90">따뜻한 마음에 감사드립니다.</p>
      </div>

      {/* 브랜드 뱃지 */}
      {displayBrandName && (
        <div className="absolute bottom-14 left-4 rounded-full border border-gray-400 bg-white/90 px-3 py-1 text-sm font-medium text-stone-700 shadow-sm">
          {displayBrandName}
        </div>
      )}

      {/* 캐로셀 네비게이션 */}
      {images.length > 1 && (
        <>
          {carouselIndex > 0 && (
            <button
              onClick={() => onIndexChange(carouselIndex - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          )}
          {carouselIndex < images.length - 1 && (
            <button
              onClick={() => onIndexChange(carouselIndex + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white backdrop-blur-sm"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          )}

          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => onIndexChange(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === carouselIndex ? 'w-5 bg-white' : 'w-2 bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductImageCarousel;
